import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

const API_BASE = process.env.REACT_APP_API_URL;

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const memberType = searchParams.get('type') || 'senior';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1
  const [phone, setPhone] = useState('');

  // Step 2
  const [otp, setOtp] = useState('');
  const [receivedOtp, setReceivedOtp] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);

  // Step 3
  const [name, setName] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [location, setLocation] = useState('');
  const [locLoading, setLocLoading] = useState(false);

  const memberTypeLabels = {
    senior: { label: 'Senior Member', emoji: '🧓', color: '#FF6B35' },
    family: { label: 'Family Member', emoji: '👨‍👩‍👧', color: '#4CAF50' },
    volunteer: { label: 'Volunteer', emoji: '🤝', color: '#2196F3' }
  };

  const current = memberTypeLabels[memberType] || memberTypeLabels.senior;

  const handleSendOtp = async () => {
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/auth/send-otp`,
        { phone: cleanPhone },
        { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
      );
      if (res.data.success) {
        const otpToShow = res.data.otp;
        if (otpToShow) {
          console.log(`OTP for ${cleanPhone}: ${otpToShow}`);
          setReceivedOtp(otpToShow);
          toast.success(`OTP: ${otpToShow}`, { duration: 10000 });
        }
        setStep(2);
      }
    } catch (err) {
      if (!err.response) {
        // Network error — server unreachable
        toast.error('Cannot reach server. Please check your internet connection and try again.');
      } else {
        toast.error(err.response?.data?.error || 'Failed to send OTP. Please try again.');
      }
      console.error('Send OTP error:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/auth/verify-otp`,
        { phone: phone.replace(/\D/g, ''), otp },
        { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
      );

      if (res.data.success) {
        if (res.data.isNewUser) {
          setTempToken(res.data.tempToken);
          setIsNewUser(true);
          setStep(3);
          toast.success('OTP verified! Complete your profile.');
        } else {
          login(res.data.user, res.data.token);
          toast.success(`Welcome back, ${res.data.user.name}!`);
          navigate('/home');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteProfile = async () => {
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/auth/register`, {
        tempToken,
        name: name.trim(),
        age_group: ageGroup,
        location: location.trim(),
        member_type: memberType
      });

      if (res.data.success) {
        login(res.data.user, res.data.token);
        toast.success(`Welcome to SaathiCircle, ${res.data.user.name}! 🎉`);
        navigate('/home');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location not supported on this device');
      return;
    }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await res.json();
          const addr = data.address;
          const locStr = [addr.suburb || addr.neighbourhood, addr.city || addr.town, addr.state]
            .filter(Boolean)
            .join(', ');
          setLocation(locStr || `${latitude.toFixed(3)}, ${longitude.toFixed(3)}`);
          toast.success('Location detected!');
        } catch {
          setLocation('Location detected');
          toast.success('Location detected!');
        } finally {
          setLocLoading(false);
        }
      },
      () => {
        toast.error('Could not detect location. Please type it manually.');
        setLocLoading(false);
      }
    );
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter') action();
  };

  return (
    <div className="page-container no-nav">
      <div style={{ padding: '0 20px 40px', minHeight: '100vh', background: '#FFF8F0' }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 0',
          gap: '12px'
        }}>
          <button
            onClick={() => step === 1 ? navigate('/') : setStep(step - 1)}
            style={{
              background: 'white',
              border: '2px solid #E8E0D8',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '20px'
            }}
          >
            ←
          </button>
          <div style={{ display: 'flex', justifyContent: 'center', flex: 1, paddingRight: '44px' }}>
            <Logo size={56} />
          </div>
        </div>

        {/* Member type badge */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 16px',
            background: `${current.color}15`,
            borderRadius: '50px',
            border: `2px solid ${current.color}30`
          }}>
            <span style={{ fontSize: '18px' }}>{current.emoji}</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: current.color }}>
              {current.label}
            </span>
          </div>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '28px' }}>
          {[1, 2, 3].map((s) => (
            <div key={s} style={{
              width: s === step ? '32px' : '10px',
              height: '10px',
              borderRadius: '5px',
              background: s <= step ? '#FF6B35' : '#E8E0D8',
              transition: 'all 0.3s ease'
            }} />
          ))}
        </div>

        {/* Step 1: Enter Phone */}
        {step === 1 && (
          <div>
            <h2 style={{ marginBottom: '8px', textAlign: 'center' }}>Enter Your Phone</h2>
            <p style={{ textAlign: 'center', marginBottom: '28px', color: '#888' }}>
              We'll send a verification code to confirm your number
            </p>

            <div className="form-group">
              <label className="form-label">📱 Mobile Number</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '17px',
                  color: '#666',
                  fontWeight: '600'
                }}>+91</span>
                <input
                  type="tel"
                  className="form-input"
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleSendOtp)}
                  maxLength={15}
                  style={{ paddingLeft: '52px', fontSize: '20px', letterSpacing: '1px' }}
                  autoFocus
                />
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSendOtp}
              disabled={loading}
              style={{ marginTop: '8px' }}
            >
              {loading ? '⏳ Sending...' : '📨 Send OTP'}
            </button>

            <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: '#bbb' }}>
              🔒 Your number is safe with us
            </p>
          </div>
        )}

        {/* Step 2: Enter OTP */}
        {step === 2 && (
          <div>
            <h2 style={{ marginBottom: '8px', textAlign: 'center' }}>Enter OTP</h2>
            <p style={{ textAlign: 'center', marginBottom: '16px', color: '#888' }}>
              OTP sent to <strong style={{ color: '#2D2D2D' }}>+91 {phone}</strong>
            </p>

            {/* OTP Display Box */}
            <div style={{
              background: '#FFF3ED',
              border: '2px dashed #FF6B35',
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '13px', color: '#888', margin: '0 0 6px 0' }}>
                Your OTP is
              </p>
              <p style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#FF6B35',
                letterSpacing: '10px',
                margin: '0'
              }}>{receivedOtp}</p>
              <p style={{ fontSize: '12px', color: '#aaa', margin: '6px 0 0 0' }}>
                Valid for 10 minutes
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">🔢 6-Digit OTP {receivedOtp ? `(${receivedOtp})` : ''}</label>
              <input
                type="number"
                className="form-input"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                onKeyDown={(e) => handleKeyDown(e, handleVerifyOtp)}
                style={{ fontSize: '28px', letterSpacing: '8px', textAlign: 'center' }}
                autoFocus
              />
            </div>

            <button
              className="btn btn-primary"
              onClick={handleVerifyOtp}
              disabled={loading}
            >
              {loading ? '⏳ Verifying...' : '✅ Verify OTP'}
            </button>

            <button
              className="btn btn-ghost"
              onClick={handleSendOtp}
              disabled={loading}
              style={{ marginTop: '12px' }}
            >
              🔄 Resend OTP
            </button>
          </div>
        )}

        {/* Step 3: Complete Profile */}
        {step === 3 && (
          <div>
            <h2 style={{ marginBottom: '8px', textAlign: 'center' }}>Your Profile</h2>
            <p style={{ textAlign: 'center', marginBottom: '28px', color: '#888' }}>
              Tell us a bit about yourself
            </p>

            <div className="form-group">
              <label className="form-label">👤 Your Name *</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Ramesh Kumar"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">🎂 Age Group</label>
              <select
                className="form-select"
                value={ageGroup}
                onChange={(e) => setAgeGroup(e.target.value)}
              >
                <option value="">Select age group</option>
                <option value="50-60">50 - 60 years</option>
                <option value="60-70">60 - 70 years</option>
                <option value="70-80">70 - 80 years</option>
                <option value="80+">80+ years</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">📍 Your Location</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Sector 12, Gurgaon"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                style={{ marginBottom: '8px' }}
              />
              <button
                className="btn btn-ghost btn-sm"
                onClick={getLocation}
                disabled={locLoading}
                style={{ width: 'auto', display: 'inline-flex' }}
              >
                {locLoading ? '⏳ Detecting...' : '📍 Auto-detect Location'}
              </button>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleCompleteProfile}
              disabled={loading}
              style={{ marginTop: '8px' }}
            >
              {loading ? '⏳ Joining...' : '🎉 Join SaathiCircle'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
